<template><div><code-example only-show="demo">
<example-modal />
</code-example>
<!-- <component-combinator component-name="DtModal" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Modals disable underlying content and are used to present a short-term task the user needs to perform without losing the context of the underlying page. Users won't be able to interact with the page until they close the modal.</p>
<p class="d-docsite--paragraph">Although highly versatile, this doesn't mean modal dialogs are fit for all purposes. Modals are purposefully disruptive and should be used thoughtfully and sparingly, specifically in moments where focus is required or an action must be taken.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">To complete a simple task or decision that requires their full attention outside the main workflow.</li>
<li class="d-docsite--list-element">Confirming a destructive action that is about to happen.</li>
<li class="d-docsite--list-element">Ask for a user's consent for an action.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">When its content or features can be part of the page without complicating the page's intent.</li>
<li class="d-docsite--list-element">When the content or message requires interaction with other parts of the application or screen.</li>
<li class="d-docsite--list-element">Form-related critical, positive, or warning messages. Keep feedback in context to forms.</li>
<li class="d-docsite--list-element">Confirming an action took place (instead: use a <RouteLink to="/components/toast.html" class="d-docsite--link d-link">Toast</RouteLink>).</li>
<li class="d-docsite--list-element">Revealing more information (instead: place content inline)</li>
<li class="d-docsite--list-element">Displaying complex forms or large amounts of information (instead: place content inline)</li>
<li class="d-docsite--list-element">Displaying content unrelated to current task (instead: place content inline as a <RouteLink to="/components/link.html" class="d-docsite--link d-link">Link</RouteLink> or <RouteLink to="/components/banner.html" class="d-docsite--link d-link">Banner</RouteLink>).</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Ideally, users trigger the modal, not the system, and should not be a surprise. Its appearance should reflect user intent to invoke it.  Uninvited modals may surprise the user and result in a quick dismissal of the window.</li>
<li class="d-docsite--list-element">Treat modals as a last resort. Consider whether there's another component or UI that might be  less disruptive for the user.</li>
<li class="d-docsite--list-element">Limit the number of interactions in a modal. Remove anything that does not support the task.</li>
<li class="d-docsite--list-element">Avoid multiple steps that require navigation within the modal dialog.</li>
<li class="d-docsite--list-element">Avoid complex decision-making that requires additional sources of information unavailable in the modal.</li>
<li class="d-docsite--list-element">Use clear header and action labels. Label links and buttons with a verb that avoids ambiguity and clearly indicates what happens when it's selected. The primary action's label should complement the modal title.</li>
<li class="d-docsite--list-element">Avoid lengthy contents that require scrolling.</li>
<li class="d-docsite--list-element">Only one modal can be present at a time.</li>
</ul>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Opened modals &quot;trap focus,&quot; meaning keyboard navigation controls are constrained to elements within the modal. Tabbing to the modal's last focusable element, and then pressing tab again would loop the focus back to the first element on the page. Focus doesn't return to the underlying page until the user explicitly dismisses the modal, in which case it would return to the place it was before the dialog opened.</li>
<li class="d-docsite--list-element">To ensure maximum compatibility, all <code v-pre>a</code> tags must have an <code v-pre>href</code>attribute. Also, any elements which you don't want to be focusable (but might be focusable by default) must have their <code v-pre>tabindex</code> set to <code v-pre>-1</code>.</li>
<li class="d-docsite--list-element">Focus should always begin on the first actionable element within the dialog. This could be an OK button, or the first field in the form. An X button in the top right corner should be last in the tab order even though it may be visually above the other elements.</li>
<li class="d-docsite--list-element">Check out the &quot;Focus management&quot; section of the following <a href="https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role#focus_management" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">MDN Dialog document</a> if you'd like to know more.</li>
<li class="d-docsite--list-element">Use <code v-pre>aria-labelledby</code> on its root element to associate a title to the modal to announce its to accessible technology. The value of aria-labelledby is to the <code v-pre>id</code> value of its heading element (e.g. <code v-pre>h2</code>).</li>
<li class="d-docsite--list-element">Dismissing Modal returns focus to the originating element that spawned the modal's display.</li>
</ul>
<component-accessible-table component-name="modal"></component-accessible-table><h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="base-style" tabindex="-1" class="d-docsite--header-3">Base Style <a class="header-anchor d-link d-docsite--link d-link" href="#base-style" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-modal
  title=&quot;Example title&quot;
  :open=&quot;isOpen&quot;
  @update:open=&quot;updateOpen&quot;
  copy=&quot;Lorem ipsum ...&quot;
&gt;
  &lt;template
    #footer
  &gt;
    &lt;dt-button
      id=&quot;cancel-button&quot;
      :kind=&quot;secondaryButtonKind&quot;
      importance=&quot;clear&quot;
    &gt;
      Cancel
    &lt;/dt-button&gt;
    &lt;dt-button
      id=&quot;confirm-button&quot;
      importance=&quot;primary&quot;
    &gt;
      Confirm
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-modal&gt;
&lt;dt-button
  @click=&quot;isOpen = !isOpen&quot;
&gt;
  Click to open
&lt;/dt-button&gt;'>
<example-modal />
</code-example>
<h3 id="fixed-header-and-footer" tabindex="-1" class="d-docsite--header-3">Fixed Header and Footer <a class="header-anchor d-link d-docsite--link d-link" href="#fixed-header-and-footer" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">This is the default behavior that adds the scroll automatically in the modal content and leaves the header and footer fixed.</p>
<code-example source-code='&lt;dt-modal
  title=&quot;Example title&quot;
  :open=&quot;isOpen&quot;
  @update:open=&quot;updateOpen&quot;
  :showFooter=&quot;true&quot;
  :fixed-header-footer=&quot;true&quot;
  copy=&quot;Sed at orci quis nunc finibus gravida eget vitae est...&quot;
&gt;
  &lt;template
    #footer
  &gt;
    &lt;dt-button
      id=&quot;cancel-button&quot;
      :kind=&quot;secondaryButtonKind&quot;
      importance=&quot;clear&quot;
    &gt;
      Cancel
    &lt;/dt-button&gt;
    &lt;dt-button
      id=&quot;confirm-button&quot;
      importance=&quot;primary&quot;
    &gt;
      Confirm
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-modal&gt;
&lt;dt-button
  @click=&quot;isOpen = !isOpen&quot;
&gt;
  Click to open
&lt;/dt-button&gt;'>
<example-modal fixed-header-footer :copy="fixedHeaderFooterCopy" />
</code-example>
<h3 id="critical" tabindex="-1" class="d-docsite--header-3">Critical <a class="header-anchor d-link d-docsite--link d-link" href="#critical" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">A modal style for destructive or irreversible actions.</p>
<code-example source-code='&lt;dt-modal
  title=&quot;Example title&quot;
  :open=&quot;isOpen&quot;
  kind=&quot;critical&quot;
  copy=&quot;Sed at orci quis nunc finibus gravida eget vitae est...&quot;
  @update:open=&quot;updateOpen&quot;
&gt;
  &lt;template
    #footer
  &gt;
    &lt;dt-button
      id=&quot;cancel-button&quot;
      :kind=&quot;secondaryButtonKind&quot;
      importance=&quot;clear&quot;
    &gt;
      Cancel
    &lt;/dt-button&gt;
    &lt;dt-button
      id=&quot;confirm-button&quot;
      kind=&quot;critical&quot;
      importance=&quot;primary&quot;
    &gt;
      Confirm
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-modal&gt;
&lt;dt-button
  @click=&quot;isOpen = !isOpen&quot;
&gt;
  Click to open
&lt;/dt-button&gt;'>
<example-modal kind="critical" />
</code-example>
<h3 id="full-screen" tabindex="-1" class="d-docsite--header-3">Full Screen <a class="header-anchor d-link d-docsite--link d-link" href="#full-screen" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">To make this modal take up as much of the screen as possible.</p>
<code-example source-code='&lt;dt-modal
  title=&quot;Example title&quot;
  :open=&quot;isOpen&quot;
  size=&quot;full&quot;
  copy=&quot;Sed at orci quis nunc finibus gravida eget vitae est...&quot;
  @update:open=&quot;updateOpen&quot;
&gt;
  &lt;template
    #footer
  &gt;
    &lt;dt-button
      id=&quot;cancel-button&quot;
      :kind=&quot;secondaryButtonKind&quot;
      importance=&quot;clear&quot;
    &gt;
      Cancel
    &lt;/dt-button&gt;
    &lt;dt-button
      id=&quot;confirm-button&quot;
      importance=&quot;primary&quot;
    &gt;
      Confirm
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-modal&gt;
&lt;dt-button
  @click=&quot;isOpen = !isOpen&quot;
&gt;
  Click to open
&lt;/dt-button&gt;'>
<example-modal size="full" />
</code-example>
<h3 id="has-banner" tabindex="-1" class="d-docsite--header-3">Has Banner <a class="header-anchor d-link d-docsite--link d-link" href="#has-banner" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When there is a need of more context information regarding the content of the Modal</p>
<code-example source-code='&lt;dt-modal
  title=&quot;Example title&quot;
  :open=&quot;isOpen&quot;
  banner-title=&quot;This banner can have different kinds.&quot;
  :bannerKind=&quot;selectedBannerKind&quot;
  copy=&quot;Sed at orci quis nunc finibus gravida eget vitae est...&quot;
  @update:open=&quot;updateOpen&quot;
&gt;
  &lt;template
    #footer
  &gt;
    &lt;dt-button
      id=&quot;cancel-button&quot;
      :kind=&quot;secondaryButtonKind&quot;
      importance=&quot;clear&quot;
    &gt;
      Cancel
    &lt;/dt-button&gt;
    &lt;dt-button
      id=&quot;confirm-button&quot;
      importance=&quot;primary&quot;
    &gt;
      Confirm
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-modal&gt;
&lt;dt-button
  @click=&quot;isOpen = !isOpen&quot;
&gt;
  Click to open
&lt;/dt-button&gt;'>
<dt-stack direction="row" gap="200" align="end">
  <dt-select-menu
    v-model="selectedBannerKind"
    label="Kind of Banner"
    :size="300"
    :options="bannerKinds"
  />
  <example-modal kind="default" :banner-kind="selectedBannerKind" banner-title="This banner can have different kinds." />
</dt-stack>
</code-example>
<h3 id="custom-header-and-content" tabindex="-1" class="d-docsite--header-3">Custom Header and Content <a class="header-anchor d-link d-docsite--link d-link" href="#custom-header-and-content" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">You're not limited to using plain title and copy text.</p>
<p class="d-docsite--paragraph">In addition to the footer, custom elements can be inserted into the header and body sections of the dialog via slots.</p>
<p class="d-docsite--paragraph"><strong>Please note:</strong> supplied header or body slots will take the place of any provided &quot;title&quot; or &quot;copy&quot; text, respectively.</p>
<code-example source-code='&lt;dt-modal
  :open=&quot;isOpen&quot;
  @update:open=&quot;updateOpen&quot;
&gt;
  &lt;template #header&gt;
    &lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-p-150 d-bgc-purple-100&quot;&gt;
      &lt;div&gt;Custom header&lt;/div&gt;
    &lt;/dt-stack&gt;
  &lt;/template&gt;
  &lt;dt-stack direction=&quot;row&quot; align=&quot;center&quot; justify=&quot;center&quot; class=&quot;d-p-400 d-bgc-gold-200&quot;&gt;
    &lt;h2&gt;Custom content&lt;/h2&gt;
  &lt;/dt-stack&gt;
&lt;/dt-modal&gt;'>
<div>
  <dt-modal
    :open="isOpen"
    @update:open="updateOpen"
  >
    <template #header>
      <dt-stack direction="row" align="center" justify="center" class="d-p-150 d-bgc-purple-100">
        <div>Custom header</div>
      </dt-stack>
    </template>
    <dt-stack direction="row" align="center" justify="center" class="d-p-400 d-bgc-gold-200">
      <h2>Custom content</h2>
    </dt-stack>
  </dt-modal>
  <dt-button
    @click="openModal"
  >
    Click to open
  </dt-button>
</div>
</code-example>
<h2 id="content-mode" tabindex="-1" class="d-docsite--header-2">Content Mode <a class="header-anchor d-link d-docsite--link d-link" href="#content-mode" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Modal content renders outside the DOM tree. Use the <code v-pre>contentMode</code> prop to apply color mode (invert, light, dark) to the positioned content. See <a href="/components/mode-island.html#positioned-components" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Positioned Components</a> for details.</p>
<code-example source-code='&lt;dt-modal content-mode=&quot;invert&quot;&gt;...&lt;/dt-modal&gt;
&lt;dt-modal content-mode=&quot;dark&quot;&gt;...&lt;/dt-modal&gt;
&lt;dt-modal content-mode=&quot;light&quot;&gt;...&lt;/dt-modal&gt;'>
<div>
  <dt-button @click="invertedModalOpen = true">Open Inverted Modal</dt-button>
  <dt-modal
    content-mode="invert"
    title="Inverted Modal"
    copy="This modal's content is in the inverted mode."
    :open="invertedModalOpen"
    @update:open="invertedModalOpen = $event"
  />
</div>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="modal" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">At minimum, modals contain a title and one button. They could also contain body text, brand illustrations, product wireframes, or multiple buttons.</p>
<component-class-table component-name="modal"></component-class-table></div></template>


<script setup>
  import ExampleModal from '@exampleComponents/ExampleModal.vue';
  import { ref } from 'vue';

  const isOpen = ref(false);
  const invertedModalOpen = ref(false);
  const selectedBannerKind = ref('positive');
  const fixedHeaderFooterCopy = ref(`Sed at orci quis nunc finibus gravida eget vitae est. Praesent
          ac laoreet mi. Cras porttitor mauris ex. Integer convallis tellus a ex egestas, id laoreet elit mollis. Mauris
          ut elementum velit. Nam vel consectetur turpis. Aenean consequat purus non nunc tincidunt rutrum. In semper
          pretium dui vel tempus. Proin et mi id mi egestas iaculis. Sed lacinia libero non molestie consequat. Sed
          efficitur purus eget lacus viverra volutpat. Nam luctus ac eros eu iaculis. Fusce non condimentum lorem.`.repeat(10))

  const openModal = () => {
    isOpen.value = true;
  };

  const updateOpen = (value) => {
    if (!value) isOpen.value = false;
  };

  const bannerKinds = Object.keys(window.DIALTONE_CONSTANTS.MODAL_BANNER_KINDS)
    .map(kind => ({ value: kind, label: kind }));
</script>